import { Paper, Grid2 as Grid, Select, MenuItem, styled, Button } from "@mui/material";
import moment from "moment";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DateTimeRangeSelect from "../input/date/DateTimeRangeSelect";
import FormInput from "../../components/input/form/FormInput";

import { IHBScanFloor } from "../../models/stat";
import { StateAction } from "../../utils/typeAlias";
import IEmployee from "../../models/employee";
import IAdminPageBuildingDetail from "../../models/adminPage/adminPageBuildingDetail";
import { IBuilding } from "../../pages/table/groupProperty/PropertyGroupAddPage";
import { IRoute } from "../../models/route";

interface SearchProps {
    buildings: IAdminPageBuildingDetail[];
    setBuildingId: StateAction<number>;
    buildingId: number;
    floors?: IHBScanFloor[];
    floorIndex?: number;
    setFloorIndex?: (index: number) => void;
    start: Date;
    setStart: StateAction<Date>;
    end: Date | undefined;
    setEnd: StateAction<Date | undefined>;
    selectedEmployees: number[];
    setSelectedEmployees: StateAction<number[]>;
    employees: IEmployee[];
    routes?: IRoute[];
    selectedRoutes: number[];
    setSelectedRoutes: StateAction<number[]>;

}

const CenterGrid = styled(Grid)(() => ({
    display: "flex",
    alignItems: "center",
}));

const SearchBar = ({
    start,
    setStart,
    end,
    setEnd,
    buildings,
    buildingId,
    selectedRoutes,
    setSelectedRoutes,
    routes,
    setBuildingId,
    floors,
    floorIndex,
    setFloorIndex,
    selectedEmployees,
    setSelectedEmployees,
    employees,
}: SearchProps) => {
    const { t } = useTranslation();
    const [selectedStart, setSelectedStart] = useState<Date>(start)
    const [selectedEnd, setSelectedEnd] = useState<Date | undefined>(end)
    const [selectedBuildingId, setSelectedBuildingId] = useState<number>(-1)
    const [routesValues, setRoutesValues] = useState<number[]>(selectedRoutes)

    useEffect(() => {
        setSelectedBuildingId(buildingId)
    }, [buildingId])

    const onRefreshFilter = () => {
        setStart(selectedStart)
        setEnd(selectedEnd)
        setBuildingId(selectedBuildingId)
        setSelectedRoutes(routesValues)
    }

    const updateEmployee = (value: string | number[]): void => {
        const ids =
            typeof value === "string"
                ? value.split(",").map((v) => Number(v))
                : value;
        setSelectedEmployees(ids);
    };

    const updateRoutes = (value: string | number[]): void => {
        const ids =
            typeof value === "string"
                ? value.split(",").map((v) => Number(v))
                : value;
        setRoutesValues && setRoutesValues(ids);
    };
    return (
        <Paper elevation={1} sx={{ padding: 2 }}>
            <Grid container display={"flex"} justifyContent="space-around" gap={{ xs: 2, md: 0 }}>
                <Grid size={{ xs: 12, sm: 5, md: 2 }}>
                    <DateTimeRangeSelect
                        minDate={moment("2023-01-01").startOf("day").toDate()}
                        sameDaySelection
                        disableFuture
                        start={selectedStart}
                        setStart={setSelectedStart}
                        setEnd={setSelectedEnd}
                        end={selectedEnd}
                        labelName={""}
                    />
                </Grid>
                <CenterGrid size={{ xs: 12, sm: 5, md: 3 }}>
                    <FormInput
                        sx={{ width: "100%" }}
                        label={t("header.building")}
                    >
                        <Select
                            notched
                            fullWidth
                            defaultValue={-1}
                            value={selectedBuildingId}
                            onChange={(event) =>
                                setSelectedBuildingId(Number(event.target.value))
                            }
                            id="building-select"
                        >
                            {buildings.map((building) => (
                                <MenuItem
                                    key={`building_${building.id}`}
                                    value={building.id}
                                >
                                    {building.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormInput>


                </CenterGrid>

                <CenterGrid size={{ xs: 12, sm: 5, md: 2 }}>
                    <FormInput
                        sx={{ width: "100%" }}
                        label="Route"
                    >
                        <Select
                            notched
                            multiple
                            fullWidth
                            disabled={employees.length <= 0}
                            defaultValue={[-1]}
                            value={routesValues}
                            onChange={(event) =>
                                updateRoutes(event.target.value)
                            }
                            id="route-select"
                        >
                            {routes && routes.map((route) => (
                                <MenuItem
                                    key={`route_${route.id}`}
                                    value={route.id}
                                >
                                    {route.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormInput>


                </CenterGrid>

                {floors && setFloorIndex && (
                    <CenterGrid size={{ xs: 12, sm: 5, md: 2 }}>
                        <FormInput
                            sx={{ width: "100%" }}
                            label={t("header.floors")}
                        >
                            <Select
                                notched
                                fullWidth
                                defaultValue={-1}
                                value={floorIndex || -1}
                                onChange={(event) =>
                                    setFloorIndex(Number(event.target.value))
                                }
                                labelId="floor-label"
                                id="floor-select"
                            >
                                {floors.map(
                                    (floor) =>
                                        buildingId === floor.buildingId && (
                                            <MenuItem
                                                key={`floor_${floor.id}`}
                                                value={floor.id}
                                            >
                                                {floor.name}
                                            </MenuItem>
                                        )
                                )}
                            </Select>
                        </FormInput>
                    </CenterGrid>
                )}
                <CenterGrid size={{ xs: 12, sm: 5, md: 2 }}>
                    <FormInput
                        sx={{ width: "100%" }}
                        label={t("header.employee")}
                    >
                        <Select
                            fullWidth
                            multiple
                            notched
                            disabled={employees.length <= 0}
                            defaultValue={[-1]}
                            value={selectedEmployees}
                            onChange={(event) =>
                                updateEmployee(event.target.value)
                            }
                            labelId="floor-label"
                            id="floor-select"
                        >
                            {employees.map((employee) => (
                                <MenuItem
                                    key={`user_${employee.id}`}
                                    value={employee.id}
                                >
                                    {employee.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormInput>
                </CenterGrid>

                <Grid size={{ xs: 12, sm: 5, md: 4 }} offset={{ md: 'auto' }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={
                            selectedStart.toString() == start.toString() && selectedEnd?.toString() == end?.toString() && selectedBuildingId == buildingId && JSON.stringify(routesValues) == JSON.stringify(selectedRoutes)
                        }
                        sx={{
                            marginRight: "1em",
                        }}
                        onClick={() => onRefreshFilter()}
                    >
                        {t("header.applyFilters")}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default SearchBar;
